import React, { useState, useEffect, useCallback } from 'react';
import { baseUrl } from '../../shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import dayjs from 'dayjs';
import { StatusChip } from '../../components/ui';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const STATUS_LABELS = {
  planned: 'Planned',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export default function ScheduleReport() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus) params.append('status', filterStatus);
    if (filterFrom) params.append('from', filterFrom);
    if (filterTo) params.append('to', filterTo);
    fetch(baseUrl + 'schedule/visits?' + params.toString(), { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setVisits(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterStatus, filterFrom, filterTo]);

  useEffect(() => { load(); }, []);

  const safeStr = s => (s || '').replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

  const downloadReport = async (id) => {
    setDownloading(id);
    try {
      const visit = visits.find(v => v.id === id);
      const filename = visit
        ? `${dayjs(visit.start_date).format('YYYY-MM-DD')}-${safeStr(visit.company)}-${safeStr(visit.site_locations || 'no-site')}.pdf`
        : `site-visit-report-${id}.pdf`;
      const res = await fetch(baseUrl + 'schedule/visits/' + id + '/report', { headers: authHeaders() });
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch (e) {
      console.error(e);
    }
    setDownloading(null);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Schedule Reports</Typography>

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <MenuItem key={v} value={v}>{l}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button fullWidth variant="contained" onClick={load}>Apply Filters</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && visits.length === 0 && (
        <Typography color="text.secondary">No visits found for the selected filters.</Typography>
      )}

      {!loading && visits.map(v => (
        <Card key={v.id} variant="outlined" sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{v.company}</Typography>
                  <StatusChip domain="visit" value={v.status} label={STATUS_LABELS[v.status] || v.status} />
                  {v.work_type_name && (
                    <Chip label={v.work_type_name} size="small" variant="outlined" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {v.site_locations || '—'} &nbsp;|&nbsp;
                  {dayjs(v.start_date).format('DD/MM/YYYY')} – {dayjs(v.end_date).format('DD/MM/YYYY')}
                  &nbsp;|&nbsp; Week {dayjs(v.start_date).isoWeek()}
                  &nbsp;|&nbsp; {v.asset_count ?? 0} asset{v.asset_count !== 1 ? 's' : ''} to inspect
                </Typography>
                {v.staff_names && (
                  <Typography variant="caption" color="text.secondary">Staff: {v.staff_names}</Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={downloading === v.id ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
                onClick={() => downloadReport(v.id)}
                disabled={downloading === v.id}
              >
                Download PDF
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
